import { useRef, useState, type KeyboardEvent, type ClipboardEvent } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlaneTop } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Button } from "@/components/ui/button/Button";
import chatComposerStyles from "./Chat.module.css";
import {
  insertPlainTextAtSelection,
  serializeContentEditableToPlainText,
} from "./contentEditableText";

export interface ComposeBarProps {
  onSend: (text: string) => void | Promise<void>;
  busy: boolean;
  placeholder: string;
  "data-testid"?: string;
}

export function ComposeBar({
  onSend,
  busy,
  placeholder,
  "data-testid": testId = "chat-compose",
}: ComposeBarProps) {
  const inputRef = useRef<HTMLDivElement>(null);
  const [hasContent, setHasContent] = useState(false);

  const readPlainText = (): string => {
    const el = inputRef.current;
    if (!el) return "";
    return serializeContentEditableToPlainText(el);
  };

  const clearInput = () => {
    if (!inputRef.current) return;
    inputRef.current.innerHTML = "";
    setHasContent(false);
  };

  const send = async () => {
    const text = readPlainText().trim();
    if (!text || busy) return;
    clearInput();
    await onSend(text);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const handleInput = () => {
    setHasContent(readPlainText().trim().length > 0);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (!inputRef.current || !text) return;
    insertPlainTextAtSelection(text, inputRef.current);
    handleInput();
  };

  const canSend = hasContent && !busy;

  return (
    <div
      className="px-3 py-2"
      data-testid={testId}
    >
      <div
        className={clsx(
          "relative rounded-2xl bg-slate-800/90",
          "focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2",
          "focus-within:ring-offset-slate-700 dark:focus-within:ring-indigo-400"
        )}
      >
        <div
          ref={inputRef}
          contentEditable={!busy}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onPaste={handlePaste}
          role="textbox"
          aria-multiline="true"
          aria-label="Message"
          data-lpignore="true"
          data-placeholder={placeholder}
          className={clsx(
            chatComposerStyles.contentEditable,
            "cursor-text w-full min-h-[80px] max-h-[200px] overflow-y-auto whitespace-pre-wrap bg-transparent px-3 py-2 focus:outline-none dark:text-white",
            busy && "cursor-not-allowed opacity-50"
          )}
        />
        <div className="flex w-full items-center gap-2 px-3 py-2 text-slate-400 dark:text-slate-400">
          <div className="flex min-w-0 flex-1 items-center gap-2" />
          <div className="flex shrink-0 items-center gap-2">
            <div
              className="shrink-0 whitespace-nowrap text-xs font-mono text-slate-400 dark:text-slate-400"
              data-testid="compose-newline-help"
            >
              Shift+Enter for new lines
            </div>
            <Button
              type="button"
              color={canSend ? "indigo" : "zinc"}
              onClick={() => void send()}
              disabled={!canSend}
              aria-label="Send"
            >
              <FontAwesomeIcon
                icon={faPaperPlaneTop}
                className={canSend ? "text-white" : undefined}
              />
              <span className="sr-only">Send</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
